import { createAsyncThunk } from "@reduxjs/toolkit";

import localForage from "localforage";

import { deleteOrganizationProject } from "Api/organizations/projects/deleteOrganizationProject.request";
import logger from "Libs/logger";
import { organizationByDescriptionIdSelector } from "Reducers/organization/organization";
// Direct selectors-file import (not the barrel) to stay circular-import safe.
import { organizationProjectSelector } from "Reducers/organization/projects/projects.selectors";

import type { AsyncThunkOptionType } from "Reducers/types";

/**
 * Deletes a project through the projects service
 * (`DELETE /organizations/:org_id/projects/:project_id`) instead of the legacy
 * subscription delete. The projects service routes to the subscription
 * internally for fixed projects. Mirrors the bookkeeping the old
 * `deleteSubscription` did: records the id in `deletedProjectIds` (so lists hide
 * it immediately) and optionally redirects.
 *
 * The demo-project delete stays on `deleteSubscription` for now — it is coupled
 * to `can_create`, which has no projects-service equivalent yet.
 */
export const deleteProject = createAsyncThunk<
  { projectId: string },
  {
    organizationId: string;
    projectId: string;
    settings?: { redirect?: string };
  },
  AsyncThunkOptionType
>(
  "app/organization/projects/delete",
  async (
    { organizationId, projectId, settings = {} },
    { getState, rejectWithValue }
  ) => {
    const { redirect } = settings;

    const organization = organizationByDescriptionIdSelector(getState(), {
      organizationDescriptionId: organizationId
    });

    if (!organization) {
      return rejectWithValue(
        `Unable to load the organization ${organizationId}`
      );
    }

    // Ownership guard: require the project to be loaded and to actually belong
    // to the resolved organization before deleting (the route projectId is
    // otherwise unverified against the org). This restores the defense the
    // legacy capability-link approach gave, since the delete button is gated on
    // the subscription, not on this OrganizationProject.
    const orgProject = organizationProjectSelector(getState(), {
      organizationId,
      projectId
    });
    if (!orgProject || orgProject.organization_id !== organization.id) {
      return rejectWithValue(
        "Could not verify the project belongs to this organization"
      );
    }

    try {
      await deleteOrganizationProject(organization.id, projectId);
    } catch (error: unknown) {
      const err = error as Record<string, unknown>;
      logger(error, { action: "delete_organization_project", organizationId });
      return rejectWithValue(
        err?.detail ??
          err?.message ??
          "An error occurred while deleting the project"
      );
    }

    // Best-effort bookkeeping so lists hide the project immediately. A storage
    // failure (IndexedDB unavailable in private windows, quota exceeded) must
    // not throw past here and block the post-delete redirect.
    try {
      const deletedProjectIds =
        (await localForage.getItem<string[]>("deletedProjectIds")) || [];
      await localForage.setItem(
        "deletedProjectIds",
        deletedProjectIds.concat([projectId])
      );
    } catch (storageError) {
      logger(storageError, {
        action: "delete_organization_project_bookkeeping",
        organizationId
      });
    }

    if (redirect) {
      window.location.replace(`${window.location.origin}${redirect}`);
    }

    return { projectId };
  }
);
